import {
  RendezvousCipher,
  parseServerFrame,
  type RendezvousRole,
  type ServerFrame,
} from "./index";

export type RendezvousStatus = "connecting" | "waiting" | "paired" | "reconnecting" | "closed";

export interface RendezvousClientOptions {
  readonly url: string;
  readonly roomId: string;
  readonly secret: string | Uint8Array;
  readonly role: RendezvousRole;
  readonly reconnect?: boolean;
  readonly reconnectDelayMs?: number;
  readonly createWebSocket?: (url: string) => WebSocket;
}

export class RendezvousClient {
  readonly #options: RendezvousClientOptions;
  readonly #cipher: RendezvousCipher;
  #socket?: WebSocket;
  #resumeToken?: string;
  #sessionId?: string;
  #closed = false;
  #reconnectTimer?: ReturnType<typeof setTimeout>;
  onPayload?: (payload: unknown) => void | Promise<void>;
  onPeerChange?: (present: boolean) => void;
  onStatus?: (status: RendezvousStatus) => void;
  onError?: (code: string, error?: Error) => void;

  constructor(options: RendezvousClientOptions) {
    this.#options = options;
    this.#cipher = new RendezvousCipher(options.roomId, options.secret);
  }

  get sessionId(): string | undefined { return this.#sessionId; }

  connect(): void {
    this.#closed = false;
    this.onStatus?.(this.#resumeToken ? "reconnecting" : "connecting");
    const socket = (this.#options.createWebSocket ?? ((url) => new WebSocket(url)))(this.#options.url);
    this.#socket = socket;
    socket.addEventListener("open", () => socket.send(JSON.stringify({
      v: 1, type: "join", roomId: this.#options.roomId, role: this.#options.role,
      ...(this.#resumeToken ? { resumeToken: this.#resumeToken } : {}),
    })));
    socket.addEventListener("message", (event) => void this.#handle(String(event.data)));
    socket.addEventListener("error", () => this.onError?.("unreachable"));
    socket.addEventListener("close", (event) => {
      if (this.#socket !== socket || this.#closed) return;
      if (!this.#options.reconnect || event.code === 1008) {
        this.onStatus?.("closed");
        this.onError?.(event.reason || "closed");
        return;
      }
      this.onStatus?.("reconnecting");
      this.#reconnectTimer = setTimeout(() => this.connect(), this.#options.reconnectDelayMs ?? 1_200);
    });
  }

  async send(payload: unknown): Promise<void> {
    if (this.#socket?.readyState !== WebSocket.OPEN) throw new Error("Rendezvous is disconnected");
    this.#socket.send(JSON.stringify({ v: 1, type: "relay", payload: await this.#cipher.encrypt(payload) }));
  }

  handoff(): void {
    if (this.#socket?.readyState === WebSocket.OPEN) this.#socket.send(JSON.stringify({ v: 1, type: "handoff" }));
    this.close("WebRTC handoff complete");
  }

  close(reason = "Session ended"): void {
    this.#closed = true;
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
    this.#socket?.close(1000, reason);
    this.onStatus?.("closed");
  }

  async #handle(raw: string): Promise<void> {
    let frame: ServerFrame;
    try { frame = parseServerFrame(raw); } catch (error) {
      this.onError?.("invalid-message", error instanceof Error ? error : undefined);
      return;
    }
    if (frame.type === "ready") {
      this.#resumeToken = frame.resumeToken;
      this.#sessionId = frame.sessionId;
      this.onStatus?.(frame.peerPresent ? "paired" : "waiting");
      this.onPeerChange?.(frame.peerPresent);
    } else if (frame.type === "peer-joined") {
      if (this.#sessionId && frame.sessionId !== this.#sessionId) return this.onError?.("session-changed");
      this.#sessionId = frame.sessionId;
      this.onStatus?.("paired");
      this.onPeerChange?.(true);
    } else if (frame.type === "peer-left") {
      this.onStatus?.("reconnecting");
      this.onPeerChange?.(false);
    } else if (frame.type === "relay") {
      try { await this.onPayload?.(await this.#cipher.decrypt(frame.payload)); }
      catch (error) { this.onError?.("invalid-payload", error instanceof Error ? error : undefined); }
    } else if (frame.type === "error" && frame.code !== "peer-unavailable") {
      this.onError?.(frame.code);
    }
  }
}
